import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import ClearIcon from '@mui/icons-material/Clear';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useNavigate } from 'react-router-dom';
import { useRepairTree, useValidateTree } from '../../../hooks/treeHooks';
import queryClient from '../../../core/http/react-query';
import { TreeRepairResult, TreeValidationResult } from '../../../types/tree';

type TreeHealthDialogProps = {
  treeId: string;
  isPaidUser: boolean;
  closeModal: () => void;
};

export default function TreeHealthDialog({
  treeId,
  isPaidUser,
  closeModal,
}: TreeHealthDialogProps) {
  const navigate = useNavigate();
  const validate = useValidateTree();
  const repair = useRepairTree();
  const [validation, setValidation] = useState<TreeValidationResult | null>(
    null,
  );
  const [repairResult, setRepairResult] = useState<TreeRepairResult | null>(
    null,
  );
  const [confirming, setConfirming] = useState(false);
  const [failed, setFailed] = useState(false);

  const runValidation = () => {
    setFailed(false);
    validate.mutate(treeId, {
      onSuccess: setValidation,
      onError: () => setFailed(true),
    });
  };

  useEffect(() => {
    runValidation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeId]);

  const handleRepair = () => {
    repair.mutate(treeId, {
      onSuccess: (result) => {
        setRepairResult(result);
        setConfirming(false);
        queryClient.invalidateQueries({ queryKey: ['familyTrees', treeId] });
        runValidation();
      },
      onError: () => setFailed(true),
    });
  };

  const issues = [
    ...(validation?.errors ?? []),
    ...(validation?.warnings ?? []),
  ];
  const totalIssues =
    (validation?.total_errors ?? 0) + (validation?.total_warnings ?? 0);
  const hiddenCount = Math.max(totalIssues - issues.length, 0);
  const isHealthy = validation?.valid && issues.length === 0;

  return (
    <Box p={2}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
        <Typography variant="h5">Tree health</Typography>
        <IconButton onClick={closeModal} aria-label="close">
          <ClearIcon />
        </IconButton>
      </Box>

      {validate.isPending && !validation && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {failed && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={runValidation}>
              Retry
            </Button>
          }
        >
          Something went wrong. Please try again.
        </Alert>
      )}

      {repairResult && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {repairResult.fixes_count > 0
            ? `Repaired ${repairResult.fixes_count} issue(s).`
            : 'Nothing needed repairing.'}
        </Alert>
      )}

      {validation && (
        <>
          {isHealthy ? (
            <Alert severity="success">
              No relationship issues found across {validation.member_count}{' '}
              members.
            </Alert>
          ) : (
            <>
              <Alert severity="warning">
                {totalIssues} relationship issue(s) found.
              </Alert>
              <List dense sx={{ maxHeight: 280, overflow: 'auto' }}>
                {issues.map((issue, i) => (
                  <ListItem key={`${i}-${issue}`} disableGutters>
                    <ListItemText primary={issue} />
                  </ListItem>
                ))}
              </List>
              {validation.truncated && hiddenCount > 0 && (
                <Typography variant="body2" color="text.secondary">
                  + {hiddenCount} more issue(s) hidden on the free plan.
                </Typography>
              )}
            </>
          )}
        </>
      )}

      {confirming && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Repair will update relationships between members (e.g. missing
          parent/child or spouse links). Continue?
        </Alert>
      )}

      <Box py={2} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button variant="outlined" onClick={closeModal}>
          Close
        </Button>
        {!isHealthy && validation && !isPaidUser && (
          <Button
            variant="contained"
            startIcon={<LockOutlinedIcon />}
            onClick={() => {
              closeModal();
              navigate('/pricing');
            }}
          >
            Upgrade to repair
          </Button>
        )}
        {!isHealthy && validation && isPaidUser && !confirming && (
          <Button variant="contained" onClick={() => setConfirming(true)}>
            Repair tree
          </Button>
        )}
        {isPaidUser && confirming && (
          <Button
            variant="contained"
            color="warning"
            onClick={handleRepair}
            disabled={repair.isPending}
          >
            {repair.isPending ? 'Repairing…' : 'Confirm repair'}
          </Button>
        )}
      </Box>
    </Box>
  );
}
